import { type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/lib/cn';

interface PublicInviteFormPanelProps {
  children: ReactNode;
  /** Chamado só depois que o form já passou pela validação nativa do navegador. */
  onSubmit: () => void | Promise<void>;
  submitting: boolean;
  submitLabel: string;
  submitLoadingLabel: string;
  submitDisabled?: boolean;
  /** Animação de morfismo exibida no lugar do form enquanto o cadastro é enviado. */
  submittingOverlay?: ReactNode;
  /** `passbook.backgroundColor` — substitui o amarelo default da Fixe no botão de envio pela cor da loja. */
  accentColor?: string;
  /**
   * `passbook.foregroundColor` — cor de texto que a própria loja já escolheu
   * pra combinar com `accentColor` (mesmo par usado no cartão Wallet). Sem
   * isso, texto branco fixo fica ilegível em lojas com cor clara (ex.: Café
   * François, bege quase branco).
   */
  foregroundColor?: string;
  className?: string;
}

/**
 * Cadastro público sempre teve UMA etapa só — o `<Wizard>` (stepper, barra de
 * progresso, "1 de 1") era mecânica de navegação sem nada para navegar. Este
 * shell é o equivalente de etapa única: um `<form>` de verdade dentro de um
 * card com fronteira visual clara (Enter já submete nativamente, sem precisar
 * de `advanceOnEnter`), sem nenhum controle de passo.
 */
export function PublicInviteFormPanel({
  children,
  onSubmit,
  submitting,
  submitLabel,
  submitLoadingLabel,
  submitDisabled = false,
  submittingOverlay,
  accentColor,
  foregroundColor,
  className,
}: PublicInviteFormPanelProps) {
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    void onSubmit();
  };

  return (
    <Card
      className={cn('public-invite-accent-scope mx-auto w-full max-w-xl', className)}
      style={accentColor ? ({ '--invite-accent': accentColor } as CSSProperties) : undefined}
    >
      {submitting && submittingOverlay ? (
        submittingOverlay
      ) : (
        <form onSubmit={handleSubmit}>
          <CardHeader>
            <CardTitle>Preencha os dados corretamente.</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {children}
            <Button
              type="submit"
              size="lg"
              className={cn('w-full', accentColor && 'hover:opacity-90')}
              style={
                accentColor
                  ? { backgroundColor: accentColor, color: foregroundColor || '#fff' }
                  : undefined
              }
              loading={submitting}
              disabled={submitDisabled}
            >
              {submitting ? submitLoadingLabel : submitLabel}
            </Button>
          </CardContent>
        </form>
      )}
    </Card>
  );
}
